// Pure serialization helpers shared by the brochure section *ToHtml functions.
// No React here — kept separate so the HTML output can be unit-tested directly
// (see html-helpers.test.ts) and the section files stay focused on the editor UI.
//
// EMAIL-CLIENT COMPATIBILITY NOTES (the "supported on all email clients" goal):
//   * Layout is TABLE-based with INLINE styles only — no flexbox/grid, no
//     external CSS, no <style> selectors relied on for structure (the <style>
//     block in email-template.ts only carries progressive mobile @media tweaks;
//     Outlook ignores it and falls back to the inline styles).
//   * Buttons are "bulletproof": a VML <v:roundrect> for Outlook/Word + a
//     styled <a> for everyone else, so the pill shape + fill survive everywhere.
//   * "Illustrations" are emoji glyphs (Unicode text, never image-blocked or
//     stripped) — they render in color in Gmail / Apple Mail / Outlook-Mac and
//     as present monochrome glyphs in Outlook-Windows.
//   * Every content <img> ships HTML width/height ATTRIBUTES (Outlook's Word
//     engine ignores CSS width/height + object-fit).

import { safeUrl } from '@crm-src/lib/sanitize'
import { BROCHURE_COLORS } from './stock-images'

// Escape user-entered plain text before it is interpolated into the email HTML
// string. The section text fields are plain <TextField>s, so a literal `<`, `&`
// or `"` must not break the surrounding markup (or inject tags). crm-api's
// render-time allowlist is the security backstop; this keeps the HTML well-formed.
export function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string),
  )
}

// Font stack used across the brochure — a safe system-ish stack that degrades to
// Arial everywhere.
export const BROCHURE_FONT =
  "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"

export interface CtaButtonOptions {
  fill?: string
  textColor?: string
  width?: number
}

// A bulletproof, rounded (pill) call-to-action button. Renders a VML roundrect
// for MSO/Outlook and a CSS-rounded <a> for every other client. Returns '' when
// there's no label OR no usable URL, so an un-filled CTA field simply omits the
// button rather than emitting a dead `href="#"` link.
export function ctaButton(rawUrl: string, label: string, opts: CtaButtonOptions = {}): string {
  const text = (label || '').trim()
  const href = safeUrl((rawUrl || '').trim())
  if (!text || !rawUrl.trim() || href === '#') return ''

  const fill = opts.fill || BROCHURE_COLORS.lime
  const textColor = opts.textColor || BROCHURE_COLORS.navy
  const width = opts.width || 240
  const safeText = escapeHtml(text)
  const safeHref = escapeHtml(href)

  return `
              <!--[if mso]>
              <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${safeHref}" style="height:48px;v-text-anchor:middle;width:${width}px;" arcsize="50%" stroke="f" fillcolor="${fill}">
                <w:anchorlock/>
                <center style="color:${textColor};font-family:${BROCHURE_FONT};font-size:16px;font-weight:bold;">${safeText}</center>
              </v:roundrect>
              <![endif]-->
              <!--[if !mso]><!-->
              <a href="${safeHref}" target="_blank" style="background-color:${fill};border-radius:999px;color:${textColor};display:inline-block;font-family:${BROCHURE_FONT};font-size:16px;font-weight:bold;line-height:48px;text-align:center;text-decoration:none;width:${width}px;-webkit-text-size-adjust:none;mso-hide:all;">${safeText}</a>
              <!--<![endif]-->`
}
